import type {
  DPData,
  DriverConfig,
  AppInfo,
  InitMessageOptions,
  RoomInitMessage,
  ScriptItem,
} from './types';
import { DEFAULT_FPS, DRIVER_SLEEP_DISABLED_TIMEOUT } from './constants';

function generateReqId(): string {
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function processUrlAndPath(url: string | undefined): { url: string; path: string | null } {
  if (url && url.startsWith('dp-cache://')) {
    return { url: '', path: url.replace('dp-cache://', '') };
  }
  return { url: url || '', path: null };
}

function getScriptTexts(scripts: ScriptItem[] | undefined): string[] {
  if (!scripts || scripts.length === 0) return [];
  return scripts
    .map((s) => s.text)
    .filter((text): text is string => typeof text === 'string' && text.trim() !== '');
}

export function createInitMessage(
  dpData: DPData,
  driverConfig: DriverConfig,
  appInfo: AppInfo,
  options: InitMessageOptions = {},
): RoomInitMessage {
  const scene = dpData.workspace?.scene?.[0];

  const tracks = (scene?.tracks ?? [])
    .filter((track) => track.type === 'background' || track.type === 'person')
    .map((track) => {
      const baseTrack = {
        type: track.type === 'background' ? 'pic' : track.type,
        properties: {
          x: track.obj.x,
          y: track.obj.y,
          w: track.obj.width,
          h: track.obj.height,
          r: track.obj.r,
        },
        z_index: track.z_index,
      };

      if (track.type === 'background') {
        const { url, path } = processUrlAndPath(track.obj.url);
        return { ...baseTrack, url, path };
      }

      if (track.type === 'person') {
        const { url: data_url, path: data_path } = processUrlAndPath(dpData.person_info?.data_url);
        return {
          ...baseTrack,
          person: {
            data_url,
            data_path,
            mask_url: '',
            mask_path: null,
            preprocess_data_url: '',
            preprocess_data_path: null,
            preprocess_mask_url: '',
            preprocess_mask_path: null,
            speak_id: dpData.person_info?.speaker_id || '',
            speak_platform: scene?.audio?.audio_platform || '',
            speak_speed: scene?.audio?.speed ?? 1.0,
            speak_volume: scene?.audio?.volume ?? 1.0,
            speak_pitch: scene?.audio?.pitch ?? 1.0,
          },
        };
      }

      return baseTrack;
    });

  return {
    cmd: 'init',
    req_id: generateReqId(),
    data: {
      base: {
        live_mode: options.liveMode ?? 'sdk',
        rtc_addr: driverConfig.rtc_addr,
        rtc_token: driverConfig.rtc_token,
      },
      wake: {
        enable: dpData.wake_up ?? false,
        keywords: dpData.wake_word || [],
        break_by_voice: dpData.interrupt ?? true,
        to_greeting_timeout_seconds: 5,
        to_greeting_words: getScriptTexts(dpData.opening_remarks_script),
        no_saying_greeting_timeout_seconds: 15,
        no_saying_greeting_words: getScriptTexts(dpData.conclusions_script),
        no_saying_sleep_timeout_seconds: DRIVER_SLEEP_DISABLED_TIMEOUT,
      },
      asr: {
        ...(appInfo.asr_service ? { client: 'local' as const, external_url: appInfo.asr_service } : {}),
        hot_words: options.hotWords || [],
      },
      llm: {
        client: 'cj',
        ...(appInfo.llm_service ? { external_url: appInfo.llm_service } : {}),
        task_id: dpData.id,
      },
      tts: {
        ...(appInfo.tts_service ? { external_url: appInfo.tts_service } : {}),
        task_id: dpData.id,
      },
      auth: {
        public_key: '',
        token: driverConfig.token,
        mac: '',
        timestamp: Date.now(),
        salt_value: '',
        random_value: '',
        unique_id: '',
        encrypted_key: '',
      },
      person: {
        config: {
          h: dpData.workspace?.screen_height || 1920,
          w: dpData.workspace?.screen_width || 1080,
          fps: DEFAULT_FPS,
          tracks,
        },
      },
      multi_speaker: {
        enable: options.faceDetection ?? false,
      },
    },
  };
}
